import React, { useState, useEffect } from "react";
import { useNetworkStatus } from "../../hooks/useNetworkStatus.js";

/**
 * Full-page offline screen that covers the entire viewport
 * Designed exactly like Chrome's offline page
 */
export default function OfflineScreen() {
  const { isOnline, wasOffline } = useNetworkStatus();
  const [showScreen, setShowScreen] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShowScreen(true);
    } else if (wasOffline && isOnline) {
      // Just reconnected - hide screen after brief delay
      setTimeout(() => {
        setShowScreen(false);
      }, 500);
    } else if (isOnline && !wasOffline) {
      // Always online, hide screen
      setShowScreen(false);
    }
  }, [isOnline, wasOffline]);

  if (!showScreen) return null;

  return (
    <div className="fixed inset-0 z-[99999] bg-white flex flex-col items-center justify-center px-4">
      {/* Main Content - Centered */}
      <div className="max-w-lg w-full">
        {/* Top row: Confused polar bear + heading */}
        <div className="flex items-center justify-center gap-6 mb-6">
          {/* Small confused black & white polar bear with questions */}
          <div className="relative">
            <svg
              className="w-16 h-16 md:w-20 md:h-20"
              viewBox="0 0 200 200"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Body */}
              <ellipse cx="100" cy="130" rx="55" ry="45" fill="#e5e7eb" />
              {/* Head */}
              <circle cx="100" cy="75" r="40" fill="#f9fafb" stroke="#111827" strokeWidth="3" />
              {/* Ears */}
              <ellipse cx="75" cy="50" rx="10" ry="13" fill="#f9fafb" stroke="#111827" strokeWidth="3" />
              <ellipse cx="125" cy="50" rx="10" ry="13" fill="#f9fafb" stroke="#111827" strokeWidth="3" />
              {/* Eyes */}
              <circle cx="88" cy="72" r="5" fill="#111827" />
              <circle cx="112" cy="72" r="5" fill="#111827" />
              {/* Eyebrows (confused) */}
              <path d="M80 60 L92 58" stroke="#111827" strokeWidth="3" strokeLinecap="round" />
              <path d="M108 58 L120 60" stroke="#111827" strokeWidth="3" strokeLinecap="round" />
              {/* Nose */}
              <ellipse cx="100" cy="85" rx="7" ry="5" fill="#111827" />
              {/* Mouth - small curve */}
              <path d="M92 93 Q100 98 108 93" stroke="#111827" strokeWidth="2" strokeLinecap="round" />
              {/* Belly */}
              <ellipse cx="100" cy="135" rx="30" ry="25" fill="#f3f4f6" stroke="#e5e7eb" strokeWidth="2" />
            </svg>

            {/* Floating question marks around head */}
            <span className="absolute -top-4 -right-2 text-slate-600 text-xl font-bold animate-bounce" style={{ animationDuration: '2s', animationDelay: '0s' }}>?</span>
            <span className="absolute -top-6 left-1 text-slate-500 text-lg font-bold animate-bounce" style={{ animationDuration: '2.5s', animationDelay: '0.3s' }}>?</span>
            <span className="absolute -top-1 left-10 text-slate-400 text-base font-bold animate-bounce" style={{ animationDuration: '2.2s', animationDelay: '0.6s' }}>?</span>
          </div>

          {/* "No Internet" Heading - Smaller and Bold */}
          <h1
            className="text-2xl md:text-3xl font-bold text-slate-800"
            style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
          >
            No Internet
          </h1>
        </div>

        {/* Try Section */}
        <div className="text-left mx-auto mb-8">
          <p className="text-base text-slate-700 mb-3" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            Try:
          </p>
          <ul className="space-y-2 text-slate-600 text-sm" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Checking the network cables, modem and router</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Reconnecting to Wi-Fi</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>
                <button
                  onClick={() => {
                    // Try to run network diagnostics (Windows)
                    if (navigator.userAgent.includes('Windows')) {
                      // Could trigger Windows network diagnostics if possible
                      window.location.reload();
                    } else {
                      window.location.reload();
                    }
                  }}
                  className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                >
                  Running Windows Network Diagnostics
                </button>
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* Error Code (bottom) */}
      <div className="absolute bottom-8 left-0 right-0 text-center">
        <p className="text-xs text-slate-500" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
          ERR_INTERNET_DISCONNECTED
        </p>
      </div>
    </div>
  );
}

